import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  StickyButtons,
  OptionsButton,
  LargeViewOnly,
  SmallViewOnly,
  ButtonDualGroup,
  ButtonGroup,
  affiliateMerchants,
} from "../../../../components/link";
import AffiliateDisclosure from "../../../../components/affiliate-disclosure";
import Center from "../../../../components/center";
import Editorial from "../../../../components/editorial";
import Head from "../../../../components/head";
import Hero from "../../../../components/hero";
import Author from "../../../../components/author";
import NoWrap from "../../../../components/no-wrap";
import ReviewItem from "../../../../components/review-item";
import PhotoListItem from "../../../../components/photo-list-item";
import Tags from "../../../../components/tags";
import Questions from "../../../../components/questions";
import Accordion from "../../../../components/accordion";
import Warning from "../../../../components/warning";
import namedRoutes from "../../../../settings/routes";
import { lookupByUrl } from "../../../../settings/route-data-lookup";

import InputCalculator from "./components/input-calculator";

import CompatibilityModule from "./components/compatibility-module";

const headerInfo = lookupByUrl(namedRoutes.bluetti.charger2.compatibility);

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      short
      positionX={25}
      positionY={78}
      background="/images/products/bluetti/charger-2/background.webp"
      titlePrefix="BLUETTI"
      title="Charger 2 Compatibility"
      subTitle="Alternator Charger"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          This guide provides the most in-depth compatibility data for the{" "}
          <strong>BLUETTI Charger 2</strong> DC-DC alternator charger, including
          performance with <strong>BLUETTI power stations</strong> and{" "}
          <strong>third‑party brands</strong> like Jackery, Anker, and EcoFlow.
          Use the <strong>compatibility calculator</strong> below to estimate
          the expected charging performance for your setup.
        </p>

        <AffiliateDisclosure />

        <p>
          For the full details of Charger 2 and a comparison to Charger 1, see
          my review:
        </p>
        <ReviewItem href={namedRoutes.bluetti.charger2.review} />
      </Center>
      <Center>
        <h2>BLUETTI Power Station Compatibility</h2>
        <p>
          The tables below outline <strong>compatibility</strong>, supported{" "}
          <strong>input limits</strong>, and <strong>cable requirements</strong>{" "}
          when paired with a BLUETTI power station.
        </p>

        <CompatibilityModule />

        <SmallViewOnly>
          <ButtonList>
            <Button
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
              icon="Bluetti"
            >
              Buy from BLUETTI
            </Button>
            <Button
              affiliate="https://amzn.to/4q6J9LD"
              href="https://www.amazon.com/dp/B0FS7C11QG/"
              secondary
              icon="Amazon"
            >
              Buy from Amazon
            </Button>
          </ButtonList>
        </SmallViewOnly>

        <p>
          <strong>Reverse charging</strong> and{" "}
          <strong>battery maintenance modes</strong> require a compatible
          BLUETTI power station or battery connected to the{" "}
          <strong>Charger 2</strong> BAT port. These features are not supported
          while using the CHG port or any third‑party power station. For a full
          breakdown of how these modes work, see my{" "}
          <Link href={namedRoutes.bluetti.charger2.review}>
            detailed full review
          </Link>
          .
        </p>

        <LargeViewOnly>
          <ButtonList>
            <Button
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
              icon="Bluetti"
            >
              Buy from BLUETTI
            </Button>
            <Button
              affiliate="https://amzn.to/4q6J9LD"
              href="https://www.amazon.com/dp/B0FS7C11QG/"
              secondary
              icon="Amazon"
            >
              Buy from Amazon
            </Button>
          </ButtonList>
        </LargeViewOnly>
      </Center>
      <Center>
        <h2>Third-Party Power Station Compatibility</h2>
        <p>
          Want to use a BLUETTI Charger 2 with your EcoFlow, Jackery, Anker, or
          other brand of solar generator? Use my{" "}
          <strong>compatibility calculator</strong> to{" "}
          <strong>verify support</strong> and see{" "}
          <strong>expected charging power</strong>. If your solar input port is
          already in use or needs an adapter, you'll find solutions in the
          sections below.
        </p>

        <InputCalculator
          minChargingVolts={12}
          highestMinChargingVolts={56}
          lowestMaxChargingVolts={12}
          maxChargingVolts={56}
          minChargingAmps={0}
          maxChargingAmps={24}
          chargerProfiles={{
            "Charger 2": {
              maxWatts: 1200,
            },
          }}
          highWarning={56}
        />

        <p>
          Actual output may be lower depending on your setup. Alternator size,
          battery voltage (12V vs 24V), vehicle battery health, and ambient
          temperature can all affect performance. Your power station’s minimum
          start‑up voltage may also limit the charging rate.
        </p>

        <p>
          <strong>More details:</strong> My{" "}
          <Link href={namedRoutes.alternatorChargers}>
            alternator charger guide
          </Link>{" "}
          covers alternator load, smart alternators, idling vs driving, and
          real‑world charging performance. It also compares chargers across all
          major brands so you can choose the setup that works best for you.
        </p>
        <ReviewItem href={namedRoutes.alternatorChargers} />

        <h3>Solar input already in use?</h3>
        <p>
          If you only have one solar input port and already use it for solar
          panels, you have two options:
        </p>
        <ol>
          <li>
            <strong>Use Charger 2 PV Port:</strong> This model includes a
            built‑in solar controller, allowing your solar panels and the
            alternator charger to operate at the same time through separate
            inputs. The solar input on Charger 2 accepts 13-50V⎓20A (600W max).
          </li>
          <li>
            <strong>Manual Swapping:</strong> Manually unplug your solar panels
            and plug in the alternator charger when driving.
          </li>
        </ol>

        {/* <mark>TODO: update FAQ</mark>
        <Accordion
          headingsAs="h2"
          items={[
            {
              heading: "Frequently asked questions (FAQ)",
              content: (
                <Fragment>
                  <div class={style.faq}>
                    <div>
                      <h3>
                        Are EcoFlow Alternator Chargers compatible with
                        third-party power stations?
                      </h3>
                      <p>
                        <strong>
                          Yes, EcoFlow Alternator Chargers are compatible with
                          many third-party power stations (such as Jackery,
                          BLUETTI, and Anker) via the XT60 output cable.
                        </strong>{" "}
                        This cable is included with 500W and 600W models but
                        sold separately for the 800W and Plus 1000. If your unit
                        uses a different connector such as Anderson, 8mm, or MC4
                        ports, you can connect using a high-quality XT60
                        adapter. Ensure your power station’s solar input voltage
                        range is compatible with the charger's output. Also note
                        that "Reverse Charge" and "Battery Maintenance" modes
                        are exclusive to compatible EcoFlow DELTA series units
                        using XT150 or 4+8 cables.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Can I use solar and alternator charging at the same
                        time?
                      </h3>
                      <p>
                        <strong>
                          Yes, you can use solar and alternator charging at the
                          same time as long as your solar generator supports
                          multiple DC inputs or you’re using the Plus 1000
                          model.
                        </strong>{" "}
                        Many EcoFlow and third‑party units can combine both
                        sources through separate solar ports, while the Plus
                        1000 adds its own 11–60V solar input (300W max) for
                        simultaneous charging without needing an extra port.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Does the 800W charge faster than the 500W model when
                        using an XT60 cable?
                      </h3>
                      <p>
                        <strong>
                          No, the 800W and 500W models both charge at 500W when
                          using the XT60 output cable.
                        </strong>{" "}
                        additionally, your actual charging speed is also
                        impacted by your power station’s input specifications.
                        If your station is limited to 500W or approximately 10A,
                        both chargers will perform identically. To unlock the
                        800W model’s full 800W potential, you must use the XT150
                        or Custom Fit (4+8) output cable with a compatible
                        EcoFlow DELTA series unit.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Why am I not reaching the full 500W or 800W charging
                        speed?
                      </h3>
                      <p>
                        <strong>
                          Maximum charging speeds are limited by your portable
                          power station’s input specifications, the output cable
                          used, and additional factors such as alternator size,
                          vehicle battery health, and high ambient temperatures.
                        </strong>{" "}
                        To reach full speeds, ensure you are using an XT150 or
                        Custom Fit (4+8) cable with a compatible high-input
                        EcoFlow power station and a healthy vehicle electrical
                        system capable of sustained high-current output.
                      </p>
                    </div>
                    <div>
                      <h3>
                        Can I use the "Reverse Charge" feature with a
                        non-EcoFlow power station?
                      </h3>
                      <p>
                        <strong>
                          No, the "Reverse Charge" and "Battery Maintenance"
                          features are only supported with a compatible EcoFlow
                          DELTA series unit while using the XT150 or Custom Fit
                          (4+8) output cable.
                        </strong>{" "}
                        Most power stations' solar input ports are physically
                        incapable of exporting power to the charger. Therefore,
                        EcoFlow restricts these features to the XT150 or 4+8
                        output cables.
                      </p>
                    </div>
                    <div>
                      <h3>
                        What happens if my portable power station has a maximum
                        input voltage below 24V?
                      </h3>
                      <p>
                        <strong>
                          Connecting a portable power station with a maximum
                          input voltage below 24V is incompatible and poses a
                          risk of hardware damage.
                        </strong>{" "}
                        If the charger’s output voltage exceeds your power
                        station's input limit, it could trigger an over-voltage
                        protection (OVP) error or, in units lacking protective
                        circuitry, permanently damage the internal charge
                        controller. Always verify your power station’s "Max
                        Volts" specification and use the EcoFlow app to set a
                        custom max voltage limit between 24V and 60V.
                      </p>
                    </div>
                  </div>
                </Fragment>
              ),
            },
            // {
            //   heading: "Popular third-party combinations",
            //   content: <Fragment></Fragment>,
            // },
          ]}
        /> */}

        <ButtonList>
          <Button
            affiliateData={affiliateMerchants.bluetti}
            href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
            icon="Bluetti"
          >
            Buy from BLUETTI
          </Button>
          <Button
            affiliate="https://amzn.to/4q6J9LD"
            href="https://www.amazon.com/dp/B0FS7C11QG/"
            secondary
            icon="Amazon"
          >
            Buy from Amazon
          </Button>
        </ButtonList>
      </Center>
      <Center>
        <h2>Vehicle Compatibility</h2>
        <p>
          Charger 2 is compatible with standard 12V (default) passenger vehicles
          and 24V systems found in heavy-duty trucks or RV house batteries.
        </p>
        <ul>
          <li>
            <strong>Automatic Voltage Detection:</strong> The charger features
            automatic voltage detection to determine when to start charging.
            While it has a preset threshold, it is highly recommended to
            manually configure the start-voltage value in the app to match your
            specific vehicle. This ensures charging begins when the engine
            starts and stops reliably when the engine is off, to prevent
            draining your starter battery.
          </li>
          <li>
            <strong>Smart Alternator Support:</strong> By default Charger 2
            relies on voltage detection. In vehicles with smart alternators,
            charging may temporarily pause during fuel-saving voltage drops and
            resume once the voltage climbs. To prevent this, you can install the
            optional{" "}
            <Link href={`${namedRoutes.bluetti.charger2.review}#diy-d+-cable`}>
              D+ signal wire
            </Link>
            .
          </li>
        </ul>
      </Center>
      <Center>
        <h2>Cables and Accessories</h2>

        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/dc-hub-for-charger-2"
              img="/images/products/bluetti/charger-2/dc-hub.webp"
              title="DC Hub"
              titleSuffix="(not Hub D1 for Apex 200/300)"
              description={
                <Fragment>
                  700W Total DC Power
                  <br />
                  Ports: USB-C, USB-A, DC5521, Cigarette Lighter, and Anderson
                  Port
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-d-signal-wire"
              img="/images/products/bluetti/charger-2/d+-signal-wire.webp"
              title="D+ Signal Wire"
              description={
                <Fragment>Engine run detection (smart alternators)</Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358176987"
              img="/images/products/bluetti/charger-2/fridgepower-cable.webp"
              title="FridgePower - Bidirectional Power Cable"
              description={
                <Fragment>
                  Anderson SB50 port <br />
                  Compatible:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/fridgepower-battery-backup?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                  >
                    FridgePower
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/bluetti-bluecell-201?id=6a2a69d42b71fb8ff494a178&variant=48862781210843"
                  >
                    BlueCell 200
                  </Link>
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              href="https://www.bluettipower.com/products/charger-2-bidirectional-power-cable?variant=48836358144219"
              img="/images/products/bluetti/charger-2/ac240-cable.webp"
              title="AC240 - Bidirectional Power Cable"
              description={
                <Fragment>
                  M28 Aviation port <br />
                  Compatible:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/ac240"
                  >
                    AC240
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b210"
                  >
                    B210
                  </Link>
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.bluetti}
              img="/images/products/bluetti/charger-2/bidirectional-cable.webp"
              title="Bidirectional Power Cable"
              titleSuffix="(Included)"
              description={
                <Fragment>
                  P090D port <br />
                  Batteries:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b300k?id=673c0fb87410b96647e90c63&variant=45943077208283"
                  >
                    B300K
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b500k-expansion-battery?id=69118a354abc44066f33eb5b&variant=47914931847387"
                  >
                    B500K
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b230-battery-pack"
                  >
                    B230
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/b300"
                  >
                    B300
                  </Link>{" "}
                  <br />
                  Power Stations:{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/apex-300-home-battery-backup?id=68804e7e63e18b529ef2604a&variant=47351562174683"
                  >
                    Apex 300
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/ac200l?id=673d48af5295be4bd97b0955&variant=46352232349915"
                  >
                    AC200L
                  </Link>
                  ,{" "}
                  <Link
                    affiliateData={affiliateMerchants.bluetti}
                    href="https://www.bluettipower.com/products/ac200max-power-station?variant=44272419995867"
                  >
                    AC200MAX
                  </Link>
                </Fragment>
              }
              buttonText="BLUETTI"
              buttonIcon="Bluetti"
            />
          </li>
        </ul>

        <p class="caption-left">More accessories expected soon.</p>
      </Center>
      <Center>
        <h2>BLUETTI Alternator Charger Reviews</h2>
        <p>
          Explore all BLUETTI alternator charger models. Learn about
          differences, features, reverse charging, and more:
        </p>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger2.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.charger1.review} />
          </li>
        </ul>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.altCharger.transit} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.bluetti.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
      <StickyButtons
        title="BLUETTI Charger 2"
        description={<Fragment>$499</Fragment>}
      >
        <Button
          affiliateData={affiliateMerchants.bluetti}
          href="https://www.bluettipower.com/products/bluetti-charger-2-alternator-solar-charger"
          icon="Bluetti"
        >
          View on BLUETTI
        </Button>
        <Button
          affiliate="https://amzn.to/4q6J9LD"
          href="https://www.amazon.com/dp/B0FS7C11QG/"
          secondary
          icon="Amazon"
        >
          View on Amazon
        </Button>
      </StickyButtons>
    </Editorial>
  </div>
);

export default Page;
